import { InputImageElement } from './esc-pos-image.types';

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ScaledDimensions extends ImageDimensions {
  scaleFactor: number;
}

/**
 * Utility functions for image dimension calculations
 */
export class ImageUtils {
  /**
   * Get natural dimensions of an image element
   */
  static getNaturalDimensions(element: InputImageElement): ImageDimensions {
    if (element instanceof HTMLImageElement) {
      return {
        width: element.naturalWidth || element.width,
        height: element.naturalHeight || element.height,
      };
    } else if (element instanceof HTMLCanvasElement) {
      return {
        width: element.width,
        height: element.height,
      };
    } else if ('width' in element && 'height' in element) {
      return {
        width: element.width,
        height: element.height,
      };
    }

    throw new Error('Cannot determine dimensions of image element');
  }

  /**
   * Calculate scaled dimensions that fit within maxWidth while maintaining aspect ratio
   * Similar to CSS object-fit: contain
   */
  static calculateContainDimensions(
    naturalDimensions: ImageDimensions,
    maxWidth: number,
    maxHeight?: number,
  ): ScaledDimensions {
    const { width: naturalWidth, height: naturalHeight } = naturalDimensions;

    if (naturalWidth === 0 || naturalHeight === 0) {
      throw new Error('Image has invalid dimensions (0x0)');
    }

    const aspectRatio = naturalWidth / naturalHeight;

    let targetWidth = maxWidth;
    let targetHeight = Math.round(maxWidth / aspectRatio);

    // If maxHeight is specified and calculated height exceeds it, scale by height instead
    if (maxHeight && targetHeight > maxHeight) {
      targetHeight = maxHeight;
      targetWidth = Math.round(maxHeight * aspectRatio);
    }

    // Ensure width is multiple of 8 (ESC/POS requirement)
    targetWidth = Math.floor(targetWidth / 8) * 8;

    // Recalculate height to maintain exact aspect ratio with adjusted width
    targetHeight = Math.round(targetWidth / aspectRatio);

    // Ensure height is multiple of 8 (ESC/POS requirement)
    targetHeight = Math.floor(targetHeight / 8) * 8;

    // Ensure minimum dimensions
    if (targetWidth < 8) targetWidth = 8;
    if (targetHeight < 8) targetHeight = 8;

    const scaleFactor = targetWidth / naturalWidth;

    return {
      width: targetWidth,
      height: targetHeight,
      scaleFactor,
    };
  }

  /**
   * Calculate scaled dimensions using a maximum width constraint
   * This is the main function for the "contain" scaling behavior
   */
  static scaleToMaxWidth(
    element: InputImageElement,
    maxWidth: number,
    maxHeight?: number,
  ): ScaledDimensions {
    const naturalDimensions = this.getNaturalDimensions(element);
    return this.calculateContainDimensions(naturalDimensions, maxWidth, maxHeight);
  }

  /**
   * Validate that dimensions are suitable for ESC/POS printing
   */
  static validateEscPosDimensions(width: number, height: number): void {
    if (width % 8 !== 0) {
      throw new Error(`Width must be a multiple of 8, got ${width}`);
    }

    if (height % 8 !== 0) {
      throw new Error(`Height must be a multiple of 8, got ${height}`);
    }

    if (width <= 0 || height <= 0) {
      throw new Error(`Dimensions must be positive, got ${width}x${height}`);
    }

    if (width > 2048) {
      throw new Error(`Width too large for thermal printer: ${width}px (max 2048px)`);
    }

    if (height > 4096) {
      throw new Error(`Height too large for thermal printer: ${height}px (max 4096px)`);
    }
  }
}
